import {FormikProps} from "formik";
import React, {FC, memo, useEffect} from "react";
import {Button, Tooltip} from "antd";
import {FormFieldNames, PossibleFormikPropsType} from "./CourseCreateForm";
import i18n from "../../../services/i18n";

type NextStepButtonProps = {
    formikProps: FormikProps<PossibleFormikPropsType>;
    fields?: Array<FormFieldNames>;
    onNextStep: () => void;
};

const NextStepButton: FC<NextStepButtonProps> = ({
    formikProps,
    fields,
    onNextStep,
}) => {
    const [fieldsAreTouched, setFieldsAreTouched] = React.useState(false);

    useEffect(() => {
        // console.log("formikProps.touched", formikProps.touched);

        const touchedFields = Object.keys(formikProps.touched);

        if (touchedFields.length > 0) {
            setFieldsAreTouched(true);
        }
    }, [formikProps.touched]);

    const validateFields = async () => {
        if (fields) {
            // FieldArray-Felder (z.B. "lessons") dürfen nicht mit setFieldTouched(field, true)
            // markiert werden, da Formik den touched-State von Array zu Boolean korrumpiert.
            const fieldArrayFields: ReadonlyArray<string> = ["lessons"];

            // Ohne eigene Validierung: jede würde die von validateForm unten abbrechen (Formik 1).
            fields.forEach((field) => {
                if (!fieldArrayFields.includes(field)) {
                    // FormFieldNames umfasst auch Formular-Zusatzfelder, die der Formik-Typ nicht kennt
                    formikProps.setFieldTouched(field as keyof PossibleFormikPropsType & string, true, false);
                }
            });
        }

        // Schon gültig vorbelegte Felder (Kurs kopieren, Bearbeiten) brauchen keinen zweiten Klick.
        // Startet parallel eine andere Validierung (z. B. Blur des Nummernfelds mit Server-Abfrage),
        // liefert Formik {isCanceled: true} statt Fehler → wiederholen, im Zweifel nicht weiter.
        let errors: Record<string, unknown> = {isCanceled: true};
        for (let attempt = 0; attempt < 3 && errors.isCanceled; attempt++) {
            // eslint-disable-next-line no-await-in-loop
            errors = await formikProps.validateForm();
        }

        if (!errors.isCanceled && !fields?.some((field) => field in errors)) {
            onNextStep();
        }
    };

    const errorsIncludesOneOfTheseFields = () => {
        const errors = Object.keys(formikProps.errors);

        return fields?.some((field) => errors.includes(field));
    };

    const looksIncomplete = !fieldsAreTouched || errorsIncludesOneOfTheseFields();

    // Ein Element für beide Zustände: wechselte es beim Blur eines Felds zwischen mousedown und
    // mouseup (Tooltip-Knopf ↔ Knopf), ging der Klick verloren. Der Klick prüft immer selbst.
    return (
        <Tooltip title={looksIncomplete ? "Bitte fülle zuerst die Pflichtfelder aus" : undefined}>
            <Button type="primary" style={looksIncomplete ? {opacity: 0.75} : undefined} onClick={validateFields}>
                {i18n.containers.courses.CourseCreate.courseStepData.nextButton()}
            </Button>
        </Tooltip>
    );
};

export default memo(NextStepButton);
